import type { ImplementedRubricSpec, People, ScienceException, SourceTemplate, Task } from '@/lib/types'

/** Who wrote a science review entry; `people` on the page resolves `user_id` to a name. */
export interface ScienceActor { email: string; user_id?: string; role?: string }
export interface ScienceReview { verdict: 'ready' | 'needs_changes'; feedback: string; at: string; actor?: ScienceActor }
export interface TemplateCollection {
  template_id: string; version: string | number; code_sha256: string; task_ids: string[]
  pull_request?: number; commit?: string; image_release?: string; revision_id?: string; removed_tasks?: string[]
}
export interface ScienceState {
  parent: string; digest: string; status: string; review: ScienceReview | null
  run_ids?: string[]; error?: string; error_kind?: string; previous_review?: ScienceReview | null; image_release?: string
}
export interface ScienceTask { task_id: string; title: string; stage: number; revision?: number; scientific: ScienceState; review_round_id?: string }
export interface Template {
  pre_rollout_review?: boolean
  task_id: string; title: string; stage: number; revision: number; collection: TemplateCollection
  review: ScienceReview | null; approved_review?: ScienceReview | null; hold: { reason: string } | null
  publication?: Task['publication']; review_round_id: string; rollout_batch?: { token: string }; importing?: string; qc_return?: { feedback: string; task_ids: string[] }; lifecycle?: string; published_revision?: { commit: string }; import_job_id?: string
}
export interface TemplateParameter { name: string; values: string[]; varies: boolean; distinct_count?: number }
export interface EnvironmentInventory { image_release: string; os?: string; architecture?: string; error?: string; applications?: { name: string; version: string }[] }
export interface TemplateDefinition {
  parameters: TemplateParameter[]; example_prompt: string; environment: EnvironmentInventory
  prompt_comparison?: { task_id: string; title: string; identical: boolean; highlights: { start: number; end: number; label: string }[];
    additions: { task_id: string; title: string; text: string }[] } | null
  task_comparisons?: { task_id: string; prompt_changed: boolean; input_changes: number | null; grading_changes: number | null }[]
  rubric_overview?: {
    task_count: number; unavailable_task_ids: string[];
    scoring: { aggregation: ImplementedRubricSpec['aggregation']; task_ids: string[] }[];
    checks: { id: string; variants: { check: ImplementedRubricSpec['checks'][number]; task_ids: string[] }[] }[]
  }
}
export interface RolloutControl { can_start: boolean; blockers: string[]; stop_required?: boolean }
export interface ScientificQualityReview {
  entry_id: string; target: string; verdict: 'ready' | 'needs_changes'; feedback: string; at: string; actor: ScienceActor
}
export interface TemplateDetail {
  people?: People
  /** Comments on the whole workflow (target "template") or on one of its tasks. */
  workflow_comments?: { entry_id: string; target?: string; feedback: string; at: string; actor: ScienceActor }[]
  execution_coverage?: { qualifying_attempts: number; tested_tasks: number; total_tasks: number; tasks: Record<string, { tested: boolean; runs: { run_id: string; job_id: string; status: string; model: string }[] }> }
  review_preparation?: { status: string; error?: string } | null
  /** Developers only: the pipeline step that stopped this workflow. */
  science_exception?: ScienceException | null
  source_template?: SourceTemplate | null
  workflow_brief?: import('./types').Brief
  source_workflow_id?: string
  builder?: { workflow_id: string; workflow_revision: number; job_id: string; status?: string; blocker?: string } | null
  can_read_submission?: boolean
  quality_reviews?: ScientificQualityReview[]
  initial_agent_review?: { preparation?: { status: string; error?: string } | null; round_id: string;
    job?: { status: string; error?: string; report?: { verdict: string; feedback: string } } | null } | null
  source_repository?: string
  progress?: { stage: string; ready: number; total: number }; removed_tasks?: string[]
  rollout_controls?: { template: RolloutControl; tasks: Record<string, RolloutControl> }
  publish_blockers?: string[]
  template: Template; definition: TemplateDefinition; tasks: ScienceTask[]; can_review: boolean; can_claim?: boolean
  round: { round_id: string; version: number; status: string; kind?: string; peer_id?: string; peer_email?: string }
  history: { entry_id: string; target?: string; kind: string; verdict?: string; feedback?: string; at: string; actor: ScienceActor; enabled?: boolean; revision_id?: string }[]
}
export interface InstanceDetail {
  material_digest?: string; revision_id?: string
  prompt_comparison?: { task_id: string; title?: string; identical?: boolean; highlights: { start: number; end: number; label: string }[]; removed?: string[] } | null
  environment?: EnvironmentInventory
  task: Task & ScienceTask; parameters: Record<string, unknown>; blockers: string[]
  descriptor: { prompt: string; metadata: { instance: { tolerance_status?: string; blocked?: string[] }; training_qualified?: boolean; verifier_network?: boolean } }
  rubric_map: ImplementedRubricSpec
  runtime_files?: { path: string; size: number; sha256: string }[]
  files: { path: string; size: number; sha256: string }[]
}
export const scienceStatus = (status?: string) => ({ build: 'Build', awaiting_review: 'Review', launching: 'Starting rollouts', running: 'Rollouts',
  quality_review: 'Quality check', qc_passed: 'Quality check passed', needs_changes: 'Needs changes', published: 'Published' }[status ?? ''] ?? status ?? 'Not checked')

export const scientificStages = [['build', 'Authoring agent'], ['quality', 'Quality check']] as const
export function scientificStage(task: ScienceTask): string {
  if (task.stage === 5) return 'published'
  return task.stage >= 4 ? 'quality' : 'build'
}

/** The label a template shows before its title in task lists. */
export const templateLabel = (collection: TemplateCollection) =>
  `Workflow · ${collection.task_ids.length} task${collection.task_ids.length === 1 ? '' : 's'}`

/** The template a research-approved workflow came from, from a list row or the workflow record. */
export function sourceTemplate(task: Task): SourceTemplate | undefined {
  const research = task.scientific_authoring?.research
  return task.source_template ?? (research ? { template_id: research.intake_id, title: research.intake_title } : undefined)
}
